import React, { useEffect, useRef } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { I18nProvider } from './i18n/I18nContext';
import { RealtimeProvider } from './realtime/RealtimeContext';
import { WarehouseProvider, useWarehouse, ChooseWarehouse } from './context/WarehouseContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import AppLayout from './components/AppLayout';
import LiveMachines from './pages/LiveMachines';
import OperatorTerminal from './pages/OperatorTerminal';
import MachineLogs from './pages/MachineLogs';
import ProblemsDowntime from './pages/ProblemsDowntime';
import ProductionScheduling from './pages/ProductionScheduling';
import Materials from './pages/Materials';
import MaterialSchedule from './pages/MaterialSchedule';
import OperatorAssignments from './pages/OperatorAssignments';
import Machines from './pages/Machines';
import Reports from './pages/Reports';
import MachineProblemReport from './pages/MachineProblemReport';
import Leaderboard from './pages/Leaderboard';
import MyAwards from './pages/MyAwards';
import ManageAwards from './pages/ManageAwards';
import UserManagement from './pages/UserManagement';
import AuditTrail from './pages/AuditTrail';
import Notifications from './pages/Notifications';
import HelpGuide from './pages/HelpGuide';
import Settings from './pages/Settings';

function Protected({ children }) {
  const { user, loading } = useAuth();
  const wh = useWarehouse();
  if (loading) return <div className="kmt-grid-bg min-h-screen" />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'PENDING') return <PendingApproval />;
  if (!wh?.ready) return <div className="kmt-grid-bg min-h-screen flex items-center justify-center" style={{ color: 'var(--kmt-muted)' }}>Loading factory…</div>;
  if (wh.needsChoice) return <ChooseWarehouse />;
  return children;
}

// Shown to Google users who have signed in but have no role yet
function PendingApproval() {
  const { user, logout } = useAuth();
  return (
    <div className="kmt-grid-bg min-h-screen flex flex-col items-center justify-center px-4 text-center" data-testid="pending-approval">
      <div className="kmt-panel p-8 max-w-md w-full">
        <div className="font-display text-2xl font-semibold" style={{ color: 'var(--kmt-gold)' }}>Awaiting Approval</div>
        <p className="text-sm mt-3" style={{ color: 'var(--kmt-muted)' }}>
          Hi {user?.name}, your account (<span className="font-mono-k">{user?.email}</span>) has been created.
          An administrator needs to assign you a role before you can access the shop floor.
        </p>
        <button onClick={logout} className="kmt-btn-ghost w-full mt-6" data-testid="pending-logout-btn">Sign out</button>
      </div>
    </div>
  );
}

// Processes the Emergent OAuth redirect (#session_id=...) then lands on the app
function AuthCallback() {
  const { completeGoogleSession } = useAuth();
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.replace('#', '')).get('session_id');
    if (!sid) { navigate('/login'); return; }
    completeGoogleSession(sid)
      .then(() => { window.history.replaceState(null, '', window.location.pathname); navigate('/'); })
      .catch(() => { navigate('/login'); });
  }, []); // eslint-disable-line
  return <div className="kmt-grid-bg min-h-screen flex items-center justify-center" style={{ color: 'var(--kmt-muted)' }}>Signing you in…</div>;
}

// Stable role-guard sets (module scope so route elements don't get new array refs each render)
const ALL_ROLES = ['ADMIN', 'SUPERVISOR', 'OPERATOR'];
const MANAGER_ROLES = ['ADMIN', 'SUPERVISOR'];
const ADMIN_ONLY = ['ADMIN'];

// Home landing depends on role: operators start on the Operator Terminal
function homeFor(role) {
  return role === 'OPERATOR' ? '/operator-terminal' : '/';
}

// Guard a route by allowed roles; redirect to the user's home if not allowed
function Allowed({ roles, children }) {
  const { user } = useAuth();
  if (user && roles.includes(user.role)) return children;
  return <Navigate to={homeFor(user?.role)} replace />;
}

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === 'OPERATOR') return <Navigate to="/operator-terminal" replace />;
  return <LiveMachines />;
}

function AppRouter() {
  const location = useLocation();
  // Detect the OAuth callback fragment synchronously, before any route guard runs
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<RoleHome />} />
        <Route path="operator-terminal" element={<Allowed roles={ALL_ROLES}><OperatorTerminal /></Allowed>} />
        <Route path="machine-logs" element={<Allowed roles={ALL_ROLES}><MachineLogs /></Allowed>} />
        <Route path="problems" element={<Allowed roles={ALL_ROLES}><ProblemsDowntime /></Allowed>} />
        <Route path="scheduling" element={<Allowed roles={ALL_ROLES}><ProductionScheduling /></Allowed>} />
        <Route path="materials" element={<Allowed roles={ALL_ROLES}><Materials /></Allowed>} />
        <Route path="material-schedule" element={<Allowed roles={MANAGER_ROLES}><MaterialSchedule /></Allowed>} />
        <Route path="assignments" element={<Allowed roles={MANAGER_ROLES}><OperatorAssignments /></Allowed>} />
        <Route path="machines" element={<Allowed roles={MANAGER_ROLES}><Machines /></Allowed>} />
        <Route path="reports" element={<Allowed roles={MANAGER_ROLES}><Reports /></Allowed>} />
        <Route path="problem-report" element={<Allowed roles={MANAGER_ROLES}><MachineProblemReport /></Allowed>} />
        <Route path="leaderboard" element={<Allowed roles={ALL_ROLES}><Leaderboard /></Allowed>} />
        <Route path="my-awards" element={<Allowed roles={ALL_ROLES}><MyAwards /></Allowed>} />
        <Route path="users" element={<Allowed roles={ADMIN_ONLY}><UserManagement /></Allowed>} />
        <Route path="manage-awards" element={<Allowed roles={ADMIN_ONLY}><ManageAwards /></Allowed>} />
        <Route path="audit" element={<Allowed roles={MANAGER_ROLES}><AuditTrail /></Allowed>} />
        <Route path="help" element={<Allowed roles={ALL_ROLES}><HelpGuide /></Allowed>} />
        <Route path="notifications" element={<Allowed roles={ALL_ROLES}><Notifications /></Allowed>} />
        <Route path="settings" element={<Allowed roles={MANAGER_ROLES}><Settings /></Allowed>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <I18nProvider>
          <RealtimeProvider>
            <BrowserRouter>
              <WarehouseProvider>
                <AppRouter />
              </WarehouseProvider>
            </BrowserRouter>
          <Toaster />
          </RealtimeProvider>
        </I18nProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
